import { useMemo, useState } from 'react';

import { AsyncBoundary, Icon, useToast } from '@kollez/design-system';
import { useOffers, useReleaseOffer, useRerunOfferChecks } from '@/lib/queries';
import { apiPost } from '@/lib/api';
import { formatLPA, formatIndianDate } from '@kollez/shared';
import { isOfferAwaitingSignoff } from '@kollez/shared/enums';

/**
 * /tpo/offers (F85b) — direct port of kollez/pages/tpo/offers.html.
 *   Left 380px scroll list grouped by company · right sticky letter panel
 *   Per-offer 4-check checklist (name / CTC / policy / signatory) → Release / Send-back
 *   Release-all-that-pass CTA in the page header
 */

type Seg = 'wait' | 'done';

interface OfferItem {
  _id: string;
  candidateName?: string;
  role?: string;
  ctc?: number;
  joiningDate?: string;
  status: string;
  checks?: Array<{ key: string; passed: boolean; note?: string }>;
  allChecksPassed?: boolean;
  partner?: { name?: string };
  student?: { fullName?: string; branch?: string };
  releasedAt?: string;
  createdAt?: string;
  letterUrl?: string;
}

export function TpoOffersPage() {
  const { data, isLoading, error, refetch } = useOffers();
  const release = useReleaseOffer();
  const toast = useToast();
  const [seg, setSeg] = useState<Seg>('wait');
  const [currentId, setCurrentId] = useState<string | null>(null);

  const rows = (data?.offers ?? []) as unknown as OfferItem[];
  const shown = useMemo(() => {
    if (seg === 'wait') return rows.filter((o) => isOfferAwaitingSignoff(o.status) || (o as { status: string }).status?.startsWith?.('draft'));
    return rows.filter((o) => /released|accept|declin/i.test(o.status));
  }, [rows, seg]);

  const passingCount = useMemo(() => rows.filter((o) => isOfferAwaitingSignoff(o.status) && o.allChecksPassed).length, [rows]);

  // Group shown by partner
  const grouped = useMemo(() => {
    const g: Record<string, OfferItem[]> = {};
    for (const o of shown) {
      const key = o.partner?.name ?? 'Company';
      (g[key] ??= []).push(o);
    }
    return g;
  }, [shown]);

  const current = currentId ? rows.find((o) => o._id === currentId) : shown[0];

  async function doRelease(id: string) {
    try {
      await release.mutateAsync(id);
      toast.show({ message: 'Released — the student will see the offer immediately.' });
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Release failed';
      toast.show({ tone: 'warning', message: msg });
    }
  }

  async function sendBack(id: string) {
    try {
      await apiPost(`/offers/${id}/send-back`, {});
      toast.show({ message: 'Sent back to the recruiter with your note.' });
    } catch {
      toast.show({ tone: 'warning', message: 'Send-back failed' });
    }
  }

  async function releaseAllPassing() {
    const ids = rows.filter((o) => isOfferAwaitingSignoff(o.status) && o.allChecksPassed).map((o) => o._id);
    if (ids.length === 0) { toast.show({ message: 'Nothing passes all checks yet' }); return; }
    for (const id of ids) {
      try { await release.mutateAsync(id); } catch { /* keep going */ }
    }
    toast.show({ message: `${ids.length} offer${ids.length === 1 ? '' : 's'} released.` });
  }

  return (
    <div className="stack">
      <style>{`
        .os { display: grid; gap: 20px; grid-template-columns: minmax(0,1fr); align-items: start; }
        @media (min-width: 1100px) { .os { grid-template-columns: 380px minmax(0,1fr); } .os-doc { position: sticky; top: 76px; } }
        .o-item { display: grid; grid-template-columns: 18px minmax(0,1fr) auto; gap: 10px; align-items: center; padding: 12px 16px; border-bottom: 1px solid rgb(var(--line-tertiary)); cursor: pointer; color: inherit; text-decoration: none; }
        .o-item[aria-current="true"] { background: rgb(var(--bg-info) / .6); box-shadow: inset 3px 0 0 rgb(var(--ink-accent)); }
        .o-item:hover { background: rgb(var(--bg-secondary)); }
        .check-list { list-style: none; margin: 0; padding: 0; }
        .check-list li { display: flex; gap: 8px; align-items: flex-start; font-size: .875rem; padding: 6px 0; }
        .check-list .ok { color: rgb(var(--ink-success)); }
        .check-list .bad { color: rgb(var(--ink-danger)); }
        @media (max-width: 1099px) { .os-doc { display: none; } }
      `}</style>

      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Offer sign-off</h1>
          <p className="subhead">Companies upload letters; students see them only after you check and release. We check names, CTC and placement policy for you.</p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-primary" onClick={releaseAllPassing} disabled={passingCount === 0}>
            <Icon name="mail-check" size={14} />Release all that pass
            <span className="badge ml-1" style={{ background: 'rgb(255 255 255 / .2)', color: 'inherit' }}>{passingCount}</span>
          </button>
        </div>
      </div>

      <div className="os">
        {/* Left list */}
        <div className="card">
          <div className="filterbar">
            <div className="segmented">
              <button aria-pressed={seg === 'wait'} onClick={() => setSeg('wait')}>
                Waiting <span className="helper ml-1">{rows.filter((o) => isOfferAwaitingSignoff(o.status)).length}</span>
              </button>
              <button aria-pressed={seg === 'done'} onClick={() => setSeg('done')}>Released</button>
            </div>
          </div>

          <AsyncBoundary
            kind="table"
            inCard
            loading={isLoading}
            error={error}
            empty={!isLoading && shown.length === 0}
            emptyTitle={seg === 'wait' ? 'No offers waiting' : 'Nothing released yet'}
            emptyHint={seg === 'wait' ? 'When a company uploads letters, they queue here. Students are told the moment you release.' : 'Released offers show up here.'}
            onRetry={() => void refetch()}
          >
            <div>
              {Object.entries(grouped).map(([company, offers]) => (
                <div key={company}>
                  <div className="px-4 pt-3 pb-1 eyebrow">{company} · {offers[0]?.role ?? '—'}</div>
                  {offers.map((o) => {
                    const flagged = !o.allChecksPassed && seg === 'wait';
                    const days = o.createdAt ? Math.floor((Date.now() - new Date(o.createdAt).getTime()) / 86400000) : 0;
                    return (
                      <div
                        key={o._id}
                        className="o-item"
                        role="button"
                        tabIndex={0}
                        aria-current={current?._id === o._id}
                        onClick={() => setCurrentId(o._id)}
                        onKeyDown={(e) => { if (e.key === 'Enter') setCurrentId(o._id); }}
                      >
                        <span className={flagged ? 'text-ink-danger' : 'text-ink-success'}>
                          {flagged ? <Icon name="triangle-alert" size={14} /> : <Icon name="circle-check" size={14} />}
                        </span>
                        <div className="min-w-0">
                          <div className="cell-title truncate">{o.candidateName ?? o.student?.fullName ?? '—'}</div>
                          <div className="cell-sub truncate">
                            {flagged
                              ? <span style={{ color: 'rgb(var(--ink-danger))' }}>Needs a look</span>
                              : `${o.student?.branch ?? '—'}${o.ctc ? ` · ${formatLPA(o.ctc)}` : ''}`}
                          </div>
                        </div>
                        <span className="cell-sub">
                          {seg === 'done' ? 'Released' : days > 0 ? `${days}d` : 'Today'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </AsyncBoundary>
        </div>

        {/* Right document panel */}
        <div className="os-doc grid gap-4">
          {current && <LetterPanel offer={current} onRelease={() => doRelease(current._id)} onSendBack={() => sendBack(current._id)} />}
        </div>
      </div>
    </div>
  );
}

function LetterPanel({ offer, onRelease, onSendBack }: { offer: OfferItem; onRelease: () => void; onSendBack: () => void }) {
  const checks = offer.checks ?? [];
  const byKey = Object.fromEntries(checks.map((c) => [c.key, c]));
  const partnerName = offer.partner?.name ?? 'Company';
  const isReleased = /released|accept|declin/i.test(offer.status);
  const rerun = useRerunOfferChecks();
  const toast = useToast();

  async function doRerun() {
    try {
      const res = await rerun.mutateAsync(offer._id);
      toast.show({
        message: res.allChecksPassed
          ? 'All checks pass now — you can release.'
          : `Checks re-ran. ${res.checks.filter((c) => !c.passed).length} still failing.`,
      });
    } catch (err) {
      toast.show({ tone: 'warning', message: err instanceof Error ? err.message : 'Rerun failed' });
    }
  }

  return (
    <>
      <div className="card">
        <div className="card-head flex-wrap">
          <div>
            <h2 className="h-section">{offer.candidateName ?? offer.student?.fullName ?? '—'}</h2>
            <p className="helper">
              {offer.student?.branch ?? '—'} · uploaded by {partnerName}
              {offer.createdAt ? `, ${formatIndianDate(offer.createdAt)}` : ''}
            </p>
          </div>
          {isReleased ? (
            <span className="badge badge-success">Released</span>
          ) : (
            <div className="flex gap-2">
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => void doRerun()}
                disabled={rerun.isPending}
                title="Re-check the letter against the current roster, drive CTC, and partner signatory list"
              >
                <Icon name="rotate-cw" size={14} />{rerun.isPending ? 'Re-running…' : 'Re-run checks'}
              </button>
              <button className="btn btn-outline btn-sm" onClick={onSendBack}>Send back to {partnerName}</button>
              <button
                className="btn btn-primary btn-sm"
                disabled={!offer.allChecksPassed}
                title={!offer.allChecksPassed ? 'Resolve the problem first' : ''}
                onClick={onRelease}
              >
                Release
              </button>
            </div>
          )}
        </div>
        <div className="card-pad">
          <ul className="check-list">
            {[
              ['name_vs_roster', 'Name matches the roster'],
              ['ctc_vs_drive', 'CTC matches the drive'],
              ['placement_policy', 'Placement policy: no other offer at this tier'],
              ['authorised_signatory', `Signed by an authorised ${partnerName} signatory`],
            ].map(([key, label]) => {
              const c = byKey[key as string];
              const passed = !c || c.passed;
              return (
                <li key={key}>
                  <span className={passed ? 'ok' : 'bad'}>
                    {passed ? <Icon name="check" size={14} /> : <Icon name="x" size={14} />}
                  </span>
                  <span>
                    {label}
                    {c && !c.passed && c.note && <> — <span className="text-ink-secondary">{c.note}</span></>}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <div className="paper" style={{ padding: '28px 22px 28px 44px', fontSize: 14.5 }}>
        <p className="helper" style={{ fontFamily: 'inherit', margin: '0 0 24px', color: '#5b6070' }}>
          {partnerName} · Offer of employment
        </p>
        <p>{offer.createdAt ? formatIndianDate(offer.createdAt) : ''}</p>
        <p>Dear <span className="var">{offer.candidateName ?? offer.student?.fullName ?? 'Candidate'}</span>,</p>
        <p>
          We are pleased to offer you the position of <span className="var">{offer.role ?? 'the role'}</span> with {partnerName}.
          Your annual cost to company will be <span className="var">₹{(offer.ctc ?? 0).toLocaleString('en-IN')}</span>, as detailed in Annexure A.
        </p>
        <p>Your expected date of joining is <span className="var">{offer.joiningDate ?? 'to be confirmed'}</span>, subject to successful completion of your programme and verification of your documents.</p>
        <p>Please confirm your acceptance by the deadline in your offer.</p>
        {/* Deep-audit fix (Page 32/N): the paper preview used to sign off
            with a hardcoded 'Head, Campus Talent Acquisition' name that
            has no source in the offer payload. A TPO reviewing the
            preview could be misled about who actually signed the real
            letter. Replaced with a neutral placeholder that reads as
            preview text, and pushes the reviewer to the downloadable PDF
            for the truth of record. */}
        <p style={{ marginTop: 32 }} className="helper">
          Signatory shown on the uploaded letter — download the PDF below to verify.
        </p>
      </div>

      {offer.letterUrl && (
        <a className="btn btn-outline justify-self-start" href={offer.letterUrl} target="_blank" rel="noopener noreferrer">
          Download original PDF
        </a>
      )}
    </>
  );
}
